<template>
  <div
    class="page-node-wrapper"
    :class="wrapperClasses"
    :style="wrapperStyles"
    :data-node-id="node.id"
    :data-node-type="node.nodeType"
  >
    <component
      :is="nodeComponent"
      v-bind="node.props"
      :node-id="node.id"
      :edit-mode="editMode"
      class="page-node--reveal"
      :class="{ 'page-node--editable': editMode && node.draggable }"
      @update:props="onUpdateProps"
    >
      <!-- Drag handle inside the node for proper positioning with display:contents -->
      <template v-if="editMode && node.draggable" #drag-handle>
        <div class="page-node-drag-handle drag-handle">
          <v-icon small>mdi-drag</v-icon>
        </div>
      </template>
      <!-- Slot for children injection -->
      <page-node-children
        v-if="hasChildren"
        :page-id="node.pageId"
        :parent-id="node.id"
        :edit-mode="editMode"
      />
    </component>
    <!-- Fallback drag handle for nodes that don't support the slot -->
    <div
      v-if="editMode && node.draggable && !nodeSupportsSlot"
      class="page-node-drag-handle page-node-drag-handle--overlay drag-handle"
    >
      <v-icon small>mdi-drag</v-icon>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import defaultNodes from './nodes';

// Node types that support the drag-handle slot
const SLOT_SUPPORTING_NODES = ['row', 'col', 'list', 'container', 'section'];

export default {
  name: 'PageNodeWrapper',
  beforeCreate() {
    // Lazy-load PageNodeChildren to resolve circular dependency
    this.$options.components.PageNodeChildren =
      require('./PageNodeChildren.vue').default;
  },
  props: {
    node: { type: Object, required: true },
    editMode: { type: Boolean, default: false },
  },
  computed: {
    ...mapGetters('pages', ['componentFor', 'childrenOf']),
    nodeComponent() {
      // Check registered components first, then fall back to defaults
      return (
        this.componentFor(this.node.nodeType) ||
        defaultNodes[this.node.nodeType] ||
        'div'
      );
    },
    hasChildren() {
      return this.childrenOf(this.node.id, this.node.pageId).length > 0;
    },
    isSelected() {
      // Future: selection state for editing
      return false;
    },
    nodeSupportsSlot() {
      return SLOT_SUPPORTING_NODES.includes(this.node.nodeType);
    },
    wrapperClasses() {
      return {
        'page-node-wrapper--draggable': this.node.draggable && this.editMode,
        'page-node-wrapper--selected': this.isSelected,
        'page-node-wrapper--col-edit':
          this.editMode && this.node.nodeType === 'col',
      };
    },
    /**
     * In edit mode, column wrappers need flex sizing for drag-drop to work.
     * We compute the same sizing that ColNode would use.
     */
    wrapperStyles() {
      if (!this.editMode || this.node.nodeType !== 'col') {
        return {};
      }

      const props = this.node.props || {};
      const styles = {};

      // Parse width value (same logic as ColNode)
      const parseWidth = (value) => {
        if (value === 'auto') return 'auto';
        if (value === 'grow') return 'grow';
        if (typeof value === 'string' && value.includes('%')) return value;
        const num = parseInt(value, 10);
        if (!isNaN(num) && num >= 1 && num <= 12) {
          return `${(num / 12) * 100}%`;
        }
        return '100%';
      };

      // Set CSS custom properties for each breakpoint
      styles['--wrapper-col-width-base'] = parseWidth(props.cols || '100%');
      if (props.sm != null) {
        styles['--wrapper-col-width-sm'] = parseWidth(props.sm);
      }
      if (props.md != null) {
        styles['--wrapper-col-width-md'] = parseWidth(props.md);
      }
      if (props.lg != null) {
        styles['--wrapper-col-width-lg'] = parseWidth(props.lg);
      }
      if (props.xl != null) {
        styles['--wrapper-col-width-xl'] = parseWidth(props.xl);
      }

      return styles;
    },
  },
  methods: {
    ...mapActions('pages', ['applyUpdate']),
    onUpdateProps(props) {
      this.applyUpdate({
        id: this.node.id,
        op: 'patch',
        props,
      });
    },
  },
};
</script>

<style lang="less">
.page-node-wrapper {
  // Use display: contents to be transparent to flex/grid layouts
  // This allows ColNode to be a direct flex child of RowNode
  display: contents;

  // In edit mode, ALL wrappers need real bounding boxes for SortableJS
  // The specific flex sizing for columns is handled by .page-node-wrapper--col-edit
  .page-renderer--editing & {
    display: block;
  }
}

// Reveal animation for page nodes
.page-node--reveal {
  animation: pageNodeReveal 1s cubic-bezier(0.075, 0.82, 0.165, 1) both;
}

@keyframes pageNodeReveal {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

// Editable nodes get relative positioning for drag handle
.page-node--editable {
  position: relative;
}

// Drag handle styling - positioned inside the node
.page-node-drag-handle {
  position: absolute;
  top: 4px;
  left: 4px;
  cursor: grab;
  opacity: 0;
  transition: opacity 0.2s ease;
  padding: 4px;
  z-index: 10;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  // Ensure the handle can receive pointer events for dragging
  pointer-events: auto;

  &:active {
    cursor: grabbing;
  }

  // Overlay version for nodes that don't support the slot
  // Positioned relative to .page-node--editable ancestor
  &--overlay {
    // Already has pointer-events: auto from base class
  }
}

// Show drag handle on hover of editable nodes
.page-node--editable:hover .page-node-drag-handle {
  opacity: 1;
}

// Selected state
.page-node-wrapper--selected > * {
  outline: 2px solid var(--v-primary-base);
  outline-offset: 2px;
}

// Children container - transparent to layout
.page-node-children {
  display: contents;
}

// ==============================================
// EDIT MODE: Root-level drag-drop support
// ==============================================
// At the root level (direct child of page-renderer), the vue-draggable
// container needs to be a real block element (not display: contents).

.page-renderer--editing {
  > .page-node-children {
    display: block;
  }
}

// ==============================================
// EDIT MODE: Column wrapper sizing for drag-drop
// ==============================================
// When in edit mode inside a row, wrappers need real dimensions
// for SortableJS to work. We use CSS custom properties computed
// from the column's props.

// Breakpoint values (matching Vuetify/ColNode)
@screen-sm: 600px;
@screen-md: 960px;
@screen-lg: 1280px;
@screen-xl: 1920px;

.page-node-wrapper--col-edit {
  // Base sizing
  flex: 0 0 var(--wrapper-col-width-base, 100%);
  max-width: var(--wrapper-col-width-base, 100%);

  // Handle special values
  &[style*='--wrapper-col-width-base: grow'] {
    flex: 1 1 0%;
    max-width: none;
  }
  &[style*='--wrapper-col-width-base: auto'] {
    flex: 0 0 auto;
    max-width: none;
  }

  // Responsive breakpoints
  @media (min-width: @screen-sm) {
    &[style*='--wrapper-col-width-sm'] {
      flex: 0 0 var(--wrapper-col-width-sm);
      max-width: var(--wrapper-col-width-sm);
    }
    &[style*='--wrapper-col-width-sm: grow'] {
      flex: 1 1 0%;
      max-width: none;
    }
    &[style*='--wrapper-col-width-sm: auto'] {
      flex: 0 0 auto;
      max-width: none;
    }
  }

  @media (min-width: @screen-md) {
    &[style*='--wrapper-col-width-md'] {
      flex: 0 0 var(--wrapper-col-width-md);
      max-width: var(--wrapper-col-width-md);
    }
    &[style*='--wrapper-col-width-md: grow'] {
      flex: 1 1 0%;
      max-width: none;
    }
    &[style*='--wrapper-col-width-md: auto'] {
      flex: 0 0 auto;
      max-width: none;
    }
  }

  @media (min-width: @screen-lg) {
    &[style*='--wrapper-col-width-lg'] {
      flex: 0 0 var(--wrapper-col-width-lg);
      max-width: var(--wrapper-col-width-lg);
    }
    &[style*='--wrapper-col-width-lg: grow'] {
      flex: 1 1 0%;
      max-width: none;
    }
    &[style*='--wrapper-col-width-lg: auto'] {
      flex: 0 0 auto;
      max-width: none;
    }
  }

  @media (min-width: @screen-xl) {
    &[style*='--wrapper-col-width-xl'] {
      flex: 0 0 var(--wrapper-col-width-xl);
      max-width: var(--wrapper-col-width-xl);
    }
    &[style*='--wrapper-col-width-xl: grow'] {
      flex: 1 1 0%;
      max-width: none;
    }
    &[style*='--wrapper-col-width-xl: auto'] {
      flex: 0 0 auto;
      max-width: none;
    }
  }

  // The ColNode inside should fill the wrapper
  // Remove its flex sizing (wrapper handles it) and padding (wrapper has it)
  > .page-col {
    flex: 1;
    width: 100%;
    max-width: 100%;
    padding-left: 0;
    padding-right: 0;
  }
}
</style>
